import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  AudioLines,
  Check,
  ChevronDown,
  FileSearch,
  Image as ImageIcon,
  MessageSquare,
  Search,
  ShieldCheck,
  Sparkles,
  Type,
  Video
} from 'lucide-react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { classNames } from '../../utils/classNames';
import { useI18n } from '../../hooks/useI18n';
import { useLlmCatalog } from '../../hooks/useLlmCatalog';
import {
  formatOwnedBy,
  getCategoriesFromModels,
  getDefaultModeForKind,
  getModelsForKind,
  getOwnedByOptions,
  loadStoredModel,
  storeSelectedModel
} from '../../constants/chatImageModels';

const CATEGORY_ICON = {
  chat: MessageSquare,
  image_generation: ImageIcon,
  completion: Type,
  embedding: Sparkles,
  video_generation: Video,
  audio_transcription: AudioLines,
  audio_speech: AudioLines,
  ocr: FileSearch,
  moderation: ShieldCheck,
  search: Search
};

const OWNER_TONE = {
  openai: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300',
  anthropic: 'bg-orange-100 text-orange-700 dark:bg-orange-950/50 dark:text-orange-300',
  google: 'bg-sky-100 text-sky-700 dark:bg-sky-950/50 dark:text-sky-300',
  xai: 'bg-neutral-200 text-neutral-800 dark:bg-neutral-800 dark:text-neutral-200',
  deepseek: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-950/50 dark:text-indigo-300',
  meta: 'bg-blue-100 text-blue-700 dark:bg-blue-950/50 dark:text-blue-300',
  moonshot: 'bg-violet-100 text-violet-700 dark:bg-violet-950/50 dark:text-violet-300',
  mistralai: 'bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300',
  'mistral ai': 'bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300',
  zai: 'bg-rose-100 text-rose-700 dark:bg-rose-950/50 dark:text-rose-300',
  cohere: 'bg-teal-100 text-teal-700 dark:bg-teal-950/50 dark:text-teal-300',
  perplexity: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-950/50 dark:text-cyan-300'
};

const ownerTone = (ownedBy) =>
  OWNER_TONE[String(ownedBy || '').trim().toLowerCase()] ||
  'bg-neutral-100 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-200';

const ownerInitials = (ownedBy) => {
  const label = formatOwnedBy(ownedBy);
  if (!label) {
    return 'AI';
  }
  const parts = label.split(/\s+/).filter(Boolean);
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
};

const FilterDropdown = ({ label, value, options, onChange }) => {
  const rootRef = useRef(null);
  const [open, setOpen] = useState(false);
  const selected = options.find((option) => option.value === value) || options[0];

  useEffect(() => {
    const onPointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, []);

  return (
    <div ref={rootRef} className="relative min-w-0">
      <span className="mb-1.5 block text-xs font-medium text-neutral-400">{label}</span>
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        aria-expanded={open}
        className="flex h-11 w-full items-center gap-2 rounded-xl border border-neutral-200 bg-white px-3 text-start text-sm text-neutral-900 transition hover:bg-neutral-50 dark:border-neutral-800 dark:bg-neutral-950 dark:text-white dark:hover:bg-neutral-900"
      >
        {selected?.icon ? <span className="shrink-0 opacity-80">{selected.icon}</span> : null}
        <span className="min-w-0 flex-1 truncate font-medium">{selected?.label}</span>
        {selected?.count != null && selected.count !== '' ? (
          <span className="shrink-0 text-xs text-neutral-400">{selected.count}</span>
        ) : null}
        <ChevronDown size={14} className={classNames('shrink-0 text-neutral-400 transition', open && 'rotate-180')} />
      </button>
      {open ? (
        <div className="absolute inset-x-0 top-full z-30 mt-1.5 max-h-72 overflow-y-auto rounded-xl border border-neutral-200 bg-white py-1 shadow-[0_12px_40px_rgba(0,0,0,0.08)] dark:border-neutral-800 dark:bg-neutral-950">
          {options.map((option) => {
            const active = option.value === value;
            return (
              <button
                key={option.value || 'all'}
                type="button"
                onClick={() => {
                  onChange(option.value);
                  setOpen(false);
                }}
                className={classNames(
                  'flex w-full items-center gap-2 px-3 py-2.5 text-start text-sm transition',
                  active
                    ? 'bg-neutral-50 font-medium text-neutral-950 dark:bg-neutral-900 dark:text-white'
                    : 'text-neutral-600 hover:bg-neutral-50 dark:text-neutral-300 dark:hover:bg-neutral-900'
                )}
              >
                {option.icon ? <span className="shrink-0 opacity-80">{option.icon}</span> : null}
                <span className="min-w-0 flex-1 truncate">{option.label}</span>
                {option.count != null && option.count !== '' ? (
                  <span className="shrink-0 text-xs text-neutral-400">{option.count}</span>
                ) : null}
                {active ? <Check size={14} className="shrink-0" /> : <span className="w-3.5 shrink-0" />}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
};

export const ModelPickerPage = () => {
  const { kind: rawKind } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t, language, formatNumber } = useI18n();
  const { models: catalogModels, loading } = useLlmCatalog();
  const kind = rawKind === 'image' ? 'image' : 'chat';
  const models = getModelsForKind(kind, catalogModels);
  const [selectedKey, setSelectedKey] = useState(() => loadStoredModel(kind, catalogModels));
  const [activeCategory, setActiveCategory] = useState(getDefaultModeForKind(kind));
  const [query, setQuery] = useState('');
  const [ownedByFilter, setOwnedByFilter] = useState('');
  const returnTo = searchParams.get('return') || (kind === 'image' ? '/images' : '/chat');

  useEffect(() => {
    setSelectedKey(loadStoredModel(kind, catalogModels));
  }, [catalogModels, kind]);

  const categories = useMemo(() => getCategoriesFromModels(models), [models]);

  const resolvedCategory = categories.some((category) => category.key === activeCategory)
    ? activeCategory
    : categories[0]?.key || getDefaultModeForKind(kind);

  const ownedByOptions = useMemo(() => {
    const inCategory = models.filter((model) => (model.category || 'chat') === resolvedCategory);
    return getOwnedByOptions(inCategory);
  }, [models, resolvedCategory]);

  const resolvedOwnedBy = ownedByOptions.some(
    (owner) => owner.toLowerCase() === ownedByFilter.toLowerCase()
  )
    ? ownedByFilter
    : '';

  const visibleModels = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return models.filter((model) => {
      if ((model.category || 'chat') !== resolvedCategory) {
        return false;
      }
      const ownedBy = String(model.ownedBy || model.badge || '').trim();
      if (resolvedOwnedBy && ownedBy.toLowerCase() !== resolvedOwnedBy.toLowerCase()) {
        return false;
      }
      if (!needle) {
        return true;
      }
      return [model.name, model.key, ownedBy, formatOwnedBy(ownedBy), model.descriptionFa, model.descriptionEn]
        .join(' ')
        .toLowerCase()
        .includes(needle);
    });
  }, [models, query, resolvedCategory, resolvedOwnedBy]);

  const categoryCounts = useMemo(() => {
    const counts = {};
    for (const model of models) {
      const key = model.category || 'chat';
      counts[key] = (counts[key] || 0) + 1;
    }
    return counts;
  }, [models]);

  const ownerCounts = useMemo(() => {
    const counts = {};
    for (const model of models) {
      if ((model.category || 'chat') !== resolvedCategory) {
        continue;
      }
      const ownedBy = String(model.ownedBy || model.badge || '').trim().toLowerCase();
      if (!ownedBy) {
        continue;
      }
      counts[ownedBy] = (counts[ownedBy] || 0) + 1;
    }
    return counts;
  }, [models, resolvedCategory]);

  const categoryTotal = categoryCounts[resolvedCategory] || 0;
  const hasExtraFilter = Boolean(query.trim() || resolvedOwnedBy);

  const handleSelect = (model) => {
    if (model.allowed === false) {
      navigate('/pricing');
      return;
    }
    storeSelectedModel(kind, model.key);
    setSelectedKey(model.key);
    navigate(returnTo, { replace: true });
  };

  const pageTitle = kind === 'image' ? t('modelsPageTitleImage') : t('modelsPageTitleChat');
  const pageHint = kind === 'image' ? t('modelPickerImageHint') : t('modelPickerChatHint');

  return (
    <DashboardLayout title={pageTitle}>
      <div className="mx-auto w-full max-w-5xl pb-10">
        <div className="mb-6 flex flex-wrap items-end justify-between gap-2">
          <p className="text-sm text-neutral-500">{pageHint}</p>
          <p className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
            {t('modelsTotalCount', { count: models.length })}
          </p>
        </div>

        <div className="mb-5 grid gap-3 sm:grid-cols-[minmax(0,1fr)_minmax(11rem,16rem)_minmax(11rem,16rem)]">
          <label className="block">
            <span className="mb-1.5 block text-xs font-medium text-neutral-400">{t('search')}</span>
            <span className="relative block">
              <Search size={16} className="pointer-events-none absolute start-3.5 top-1/2 -translate-y-1/2 text-neutral-400" />
              <input
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={t('modelsSearchPlaceholder')}
                className="h-11 w-full rounded-xl border border-neutral-200 bg-white py-2.5 ps-10 pe-3.5 text-sm text-neutral-900 outline-none transition placeholder:text-neutral-400 focus:border-neutral-400 dark:border-neutral-800 dark:bg-neutral-950 dark:text-white dark:focus:border-neutral-600"
              />
            </span>
          </label>

          {categories.length > 0 ? (
            <FilterDropdown
              label={t('modelsModeFilter')}
              value={resolvedCategory}
              onChange={(next) => {
                setActiveCategory(next);
                setOwnedByFilter('');
              }}
              options={categories.map((category) => {
                const Icon = CATEGORY_ICON[category.key] || Sparkles;
                return {
                  value: category.key,
                  label: language === 'fa' ? category.titleFa : category.titleEn,
                  count: formatNumber(categoryCounts[category.key] || 0),
                  icon: <Icon size={14} />
                };
              })}
            />
          ) : null}

          {ownedByOptions.length > 0 ? (
            <FilterDropdown
              label={t('modelsOwnedByFilter')}
              value={resolvedOwnedBy}
              onChange={setOwnedByFilter}
              options={[
                {
                  value: '',
                  label: t('modelsOwnedByAll'),
                  count: formatNumber(categoryTotal)
                },
                ...ownedByOptions.map((owner) => ({
                  value: owner,
                  label: formatOwnedBy(owner),
                  count: formatNumber(ownerCounts[owner.toLowerCase()] || 0),
                  icon: (
                    <span
                      className={classNames(
                        'inline-flex h-5 w-5 items-center justify-center rounded-md text-[9px] font-bold',
                        ownerTone(owner)
                      )}
                    >
                      {ownerInitials(owner)}
                    </span>
                  )
                }))
              ]}
            />
          ) : null}
        </div>

        <div className="mb-3 text-sm font-medium text-neutral-500">
          {hasExtraFilter
            ? t('modelsFilteredCount', {
                shown: visibleModels.length,
                total: categoryTotal
              })
            : t('modelsResultCount', { count: categoryTotal })}
        </div>

        {loading && visibleModels.length === 0 ? (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }).map((_, index) => (
              <div
                key={index}
                className="h-40 animate-pulse rounded-xl border border-neutral-200 bg-neutral-50 dark:border-neutral-800 dark:bg-neutral-900"
              />
            ))}
          </div>
        ) : visibleModels.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-neutral-200 px-6 py-16 text-center dark:border-neutral-800">
            <Search size={22} className="mb-3 text-neutral-300" />
            <h2 className="text-sm font-semibold text-neutral-900 dark:text-white">{t('modelsEmptySearch')}</h2>
            <p className="mt-1 max-w-sm text-sm text-neutral-500">{t('modelsEmptySearchBody')}</p>
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {visibleModels.map((model) => {
              const ownedBy = model.ownedBy || model.badge;
              const isSelected = selectedKey === model.key;
              const locked = model.allowed === false;
              const description = language === 'fa' ? model.descriptionFa : model.descriptionEn;

              return (
                <button
                  key={`${model.category}-${model.key}`}
                  type="button"
                  onClick={() => handleSelect(model)}
                  className={classNames(
                    'group flex h-full flex-col rounded-xl border bg-white p-4 text-start transition dark:bg-neutral-950',
                    locked ? 'opacity-80' : '',
                    isSelected
                      ? 'border-neutral-900 shadow-sm dark:border-white'
                      : 'border-neutral-200 hover:border-neutral-300 hover:bg-neutral-50/80 dark:border-neutral-800 dark:hover:border-neutral-700 dark:hover:bg-neutral-900/50'
                  )}
                >
                  <div className="mb-3 flex items-start justify-between gap-3">
                    <span
                      className={classNames(
                        'inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-xs font-bold',
                        ownerTone(ownedBy)
                      )}
                    >
                      {ownerInitials(ownedBy)}
                    </span>
                    {locked ? (
                      <span className="inline-flex h-7 items-center rounded-full bg-neutral-100 px-2.5 text-[11px] font-semibold text-neutral-500 dark:bg-neutral-900 dark:text-neutral-400">
                        {t('modelLocked')}
                      </span>
                    ) : isSelected ? (
                      <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-neutral-950 text-white dark:bg-white dark:text-neutral-950">
                        <Check size={14} strokeWidth={2.6} />
                      </span>
                    ) : (
                      <span className="inline-flex h-7 items-center rounded-full border border-neutral-200 px-2.5 text-[11px] font-medium text-neutral-400 opacity-0 transition group-hover:opacity-100 dark:border-neutral-800">
                        {t('selectModel')}
                      </span>
                    )}
                  </div>

                  <div className="min-w-0">
                    <div className="truncate text-[15px] font-semibold tracking-tight text-neutral-950 dark:text-white">
                      {model.name}
                    </div>
                    <div className="mt-1 text-xs text-neutral-400">
                      {formatOwnedBy(ownedBy) || model.provider}
                    </div>
                  </div>

                  {locked && model.lockedReason ? (
                    <p className="mt-3 line-clamp-2 flex-1 text-sm leading-6 text-neutral-500">{model.lockedReason}</p>
                  ) : description ? (
                    <p className="mt-3 line-clamp-2 flex-1 text-sm leading-6 text-neutral-500">{description}</p>
                  ) : (
                    <div className="flex-1" />
                  )}

                  {isSelected ? (
                    <span className="mt-4 text-xs font-semibold text-neutral-900 dark:text-white">
                      {t('selectedModel')}
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};
